import React, { useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useHasPerm } from "@/components/PermissionGate";
import Logo from "@/components/Logo";
import { LayoutDashboard, Users, Package, Settings, LogOut, Menu, X, ShoppingCart, ArrowLeftRight, FileClock, Receipt, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", perm: "dashboard:view" },
  { to: "/fornitori", label: "Fornitori", icon: Users, testid: "nav-fornitori", perm: "fornitori:view" },
  { to: "/prodotti", label: "Prodotti", icon: Package, testid: "nav-prodotti", perm: "prodotti:view" },
  { to: "/ordini", label: "Ordini", icon: ShoppingCart, testid: "nav-ordini", perm: "ordini:view" },
  { to: "/fatture", label: "Fatture", icon: Receipt, testid: "nav-fatture", perm: "prodotti:import" },
  {
    to: "/movimentazioni", label: "Movimentazioni", icon: ArrowLeftRight, testid: "nav-movimentazioni", perm: "movimentazioni:view",
    children: [
      { to: "/storico-movimenti", label: "Storico Movimenti", icon: History, testid: "nav-storico-movimenti", perm: "movimentazioni:view" },
      { to: "/log-magazzino", label: "Log Magazzino", icon: FileClock, testid: "nav-log-magazzino", perm: "movimentazioni:view" },
    ],
  },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings, testid: "nav-impostazioni", perm: "impostazioni:view" },
];

export default function Layout() {
  const { user, logout, stopImpersonate } = useAuth();
  const hasPerm = useHasPerm();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // Filter nav items + children based on user permissions
  const visibleNav = NAV
    .filter((n) => !n.perm || hasPerm(n.perm))
    .map((n) => ({
      ...n,
      children: (n.children || []).filter((c) => !c.perm || hasPerm(c.perm)),
    }));

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleStopImpersonate = async () => {
    const ok = await stopImpersonate();
    if (ok) navigate("/impostazioni");
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--ds-bg)" }}>
      {user?.impersonated_by && (
        <div
          className="w-full px-4 py-2 flex items-center justify-between text-sm font-medium sticky top-0 z-40"
          style={{ background: "#B46B52", color: "white" }}
          data-testid="impersonation-banner"
        >
          <span>
            Sessione attiva come <b>{user.name || user.email}</b> · Impersonato da <b>{user.impersonated_by}</b>
          </span>
          <button
            onClick={handleStopImpersonate}
            className="px-3 py-1 rounded-full text-xs font-medium ml-3"
            style={{ background: "white", color: "#B46B52" }}
            data-testid="stop-impersonate"
          >
            Torna al tuo account
          </button>
        </div>
      )}      {/* Mobile top bar */}
      <header
        className="lg:hidden flex items-center justify-between p-4 border-b sticky top-0 z-30"
        style={{ background: "var(--ds-surface)", borderColor: "var(--ds-border)" }}
      >
        <Logo size={32} />
        <button
          onClick={() => setOpen(!open)}
          className="p-2 rounded-lg"
          style={{ color: "var(--ds-text-primary)" }}
          data-testid="mobile-menu-toggle"
          aria-label="Apri menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`${
            open ? "block" : "hidden"
          } lg:block fixed lg:sticky top-0 left-0 z-20 h-screen w-64 border-r p-6`}
          style={{ background: "var(--ds-sidebar)", borderColor: "var(--ds-border)" }}
          data-testid="sidebar"
        >
          <div className="mb-10 hidden lg:block">
            <Logo />
          </div>

          <nav className="flex flex-col gap-1">
            {visibleNav.map(({ to, label, icon: Icon, testid, children }) => (
              <React.Fragment key={to}>
                <NavLink
                  to={to}
                  end={to === "/"}
                  onClick={() => setOpen(false)}
                  data-testid={testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                      isActive ? "bg-white shadow-sm" : "hover:bg-white/60"
                    }`
                  }
                  style={({ isActive }) => ({
                    color: isActive ? "var(--ds-primary)" : "var(--ds-text-primary)",
                  })}
                >
                  <Icon size={18} strokeWidth={1.6} />
                  {label}
                </NavLink>
                {children?.map(({ to: cto, label: clabel, icon: CIcon, testid: ctestid }) => (
                  <NavLink
                    key={cto}
                    to={cto}
                    onClick={() => setOpen(false)}
                    data-testid={ctestid}
                    className={({ isActive }) =>
                      `flex items-center gap-2 pl-9 pr-3 py-1.5 rounded-lg text-xs transition-colors ${
                        isActive ? "bg-white shadow-sm font-medium" : "hover:bg-white/60"
                      }`
                    }
                    style={({ isActive }) => ({
                      color: isActive ? "var(--ds-primary)" : "var(--ds-text-secondary)",
                    })}
                  >
                    <CIcon size={14} strokeWidth={1.6} />
                    {clabel}
                  </NavLink>
                ))}
              </React.Fragment>
            ))}
          </nav>

          <div className="absolute bottom-6 left-6 right-6">
            <NavLink
              to="/profilo"
              className="ds-card p-4 mb-3 block hover:bg-white transition"
              data-testid="user-profilo-link"
            >
              <div className="label-tag">Connesso come</div>
              <div className="font-primary text-sm font-medium truncate mt-1" data-testid="user-email">
                {user?.email || "—"}
              </div>
              <div className="text-[11px] mt-1" style={{ color: "var(--ds-text-secondary)" }}>
                Gestisci profilo & password
              </div>
            </NavLink>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="w-full justify-start gap-2 rounded-lg"
              style={{ borderColor: "var(--ds-border)" }}
              data-testid="logout-button"
            >
              <LogOut size={16} /> Esci
            </Button>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 min-w-0 p-6 md:p-8 lg:p-10 ds-fade-in">
          <Outlet />
        </main>
      </div>
      <Toaster richColors position="top-right" />
    </div>
  );
}
